import sanitizeHtml from 'sanitize-html';
import { Node as SlateNode, Text } from 'slate';
import { SLATE_TEXT_FORMATS, VOID_ELEMENTS } from './const';

const sanitize = (dirty) =>
  sanitizeHtml(dirty, {
    allowedTags: [],
    allowedAttributes: {},
  });

const formatStyle = (stylesObj) => {
  const styles = Object.entries(stylesObj)
    .filter(([, value]) => value != null && value !== '')
    .map(([name, value]) => `${name}:${value}`);

  if (styles.length === 0) {
    return '';
  }

  return styles.join(';');
};

const formatAttributes = (attributes) => {
  if (!attributes) {
    return '';
  }

  const mapped = Object.entries(attributes).map(([key, value]) => `${key}="${value}"`);
  return mapped.join(' ');
};

const formatElement = (tagName, attributes, children) => {
  const attributesStr = formatAttributes(attributes);
  const elementStart = attributesStr.length > 0 ? `${tagName} ${attributesStr}` : tagName;
  return `<${elementStart}>${children}</${tagName}>`;
};

const serializeDDVariation = (variation, children) => {
  const attributes = {
    'data-variation': '',
    'data-variation-score': variation.score,
  };
  if (variation.isSaved) {
    attributes['data-saved-variation-id'] = variation.id;
  } else {
    attributes['data-variation-id'] = variation.id;
  }
  if (variation.assetType) {
    attributes['data-asset-type'] = variation.assetType;
  }
  if (variation.generationTool) {
    attributes['data-generation-tool'] = variation.generationTool;
  }
  if (variation.language) {
    attributes['data-language'] = variation.language;
  }
  if (variation.isScoreLocked) {
    attributes['data-score-locked'] = '';
  }
  if (variation.scoringMethod) {
    attributes['data-scoring-method'] = variation.scoringMethod.type;
    attributes['data-scoring-method-id'] = variation.scoringMethod.id;
    attributes['data-scoring-method-name'] = encodeURI(sanitize(variation.scoringMethod.name));
  }
  if (variation.campaignKeywords) {
    attributes['data-campaign-keywords'] = encodeURI(
      sanitize(JSON.stringify(variation.campaignKeywords))
    );
  }
  if (variation.secondaryScoringMethod) {
    attributes['data-secondary-scoring-method-name'] = encodeURI(
      sanitize(variation.secondaryScoringMethod.name)
    );
  }
  return formatElement('div', attributes, children);
};

const serializeTextNode = (node) => {
  let string = sanitize(node.text);
  if (node.bold) {
    string = `<strong>${string}</strong>`;
  }
  if (node.italic) {
    string = `<em>${string}</em>`;
  }
  if (node.underline) {
    string = `<u>${string}</u>`;
  }
  if (node.strikethrough) {
    string = `<s>${string}</s>`;
  }
  return string;
};

const serialize = (node) => {
  if (Text.isText(node)) {
    return serializeTextNode(node);
  }

  const children = node.children.map((child) => serialize(child)).join('');
  const stylesStr = formatStyle({
    'text-align': node.align,
  });
  const attributes = stylesStr.length > 0 ? { style: stylesStr } : null;
  const variation = node.variation || {};

  switch (node.type) {
    case SLATE_TEXT_FORMATS.HEADING_ONE:
      return formatElement('h1', attributes, children);
    case SLATE_TEXT_FORMATS.HEADING_TWO:
      return formatElement('h2', attributes, children);
    case SLATE_TEXT_FORMATS.HEADING_THREE:
      return formatElement('h3', attributes, children);
    case SLATE_TEXT_FORMATS.HEADING_FOUR:
      return formatElement('h4', attributes, children);
    case SLATE_TEXT_FORMATS.HEADING_FIVE:
      return formatElement('h5', attributes, children);
    case SLATE_TEXT_FORMATS.HEADING_SIX:
      return formatElement('h6', attributes, children);

    case SLATE_TEXT_FORMATS.BULLETED_LIST:
      return formatElement('ul', attributes, children);
    case SLATE_TEXT_FORMATS.NUMBERED_LIST:
      return formatElement('ol', attributes, children);
    case SLATE_TEXT_FORMATS.LIST_ITEM:
      return formatElement('li', attributes, children);

    case SLATE_TEXT_FORMATS.BLOCK_QUOTE:
      return formatElement('blockquote', attributes, children);

    case SLATE_TEXT_FORMATS.PARAGRAPH:
      return formatElement('p', attributes, children);

    case VOID_ELEMENTS.DIVIDER: {
      const localAttributes = {
        ...attributes,
        'data-divider': true,
      };
      return formatElement('div', localAttributes, children);
    }

    case SLATE_TEXT_FORMATS.LINK: {
      const localAttributes = {
        ...attributes,
        href: sanitize(node.url),
      };
      return formatElement('a', localAttributes, children);
    }

    case SLATE_TEXT_FORMATS.DD_VARIATION:
      return serializeDDVariation(variation, children);

    default:
      return children;
  }
};

export const slateToHtml = (nodes) => {
  return nodes.map(serialize).join('');
};

export const slateToText = (nodes) => {
  const DELIMITER = '\n';
  return nodes.map(SlateNode.string).join(DELIMITER);
};
